import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import api from '@/api/axios';
import { PencilSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

// For WhatsApp customers, the platform_id IS the phone number in
// international format. For Messenger it's a PSID — never a phone.
const formatWhatsappPhone = (customer) => {
  if (customer?.phone) return customer.phone;
  if (customer?.platform !== 'whatsapp') return null;
  const raw = (customer?.platform_id || '').toString();
  if (!/^\d{8,15}$/.test(raw)) return null;
  const PREFIXES = ['502', '503', '504', '505', '506', '507', '52', '1'];
  let cc = '';
  let rest = raw;
  for (const p of PREFIXES) {
    if (raw.startsWith(p)) { cc = p; rest = raw.slice(p.length); break; }
  }
  const groups = [];
  for (let i = rest.length; i > 0; i -= 4) {
    groups.unshift(rest.slice(Math.max(0, i - 4), i));
  }
  return cc ? `+${cc} ${groups.join(' ')}` : `+${raw}`;
};

const Customers = () => {
  const { selectedBusiness } = useOutletContext();
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    notes: '',
    tags: ''
  });

  useEffect(() => {
    if (selectedBusiness) fetchCustomers();
  }, [selectedBusiness]);

  const fetchCustomers = async () => {
    if (!selectedBusiness) return;
    try {
      const response = await api.get('/customers', { params: { business_id: selectedBusiness.id } });
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean)
      };

      if (selectedCustomer) {
        await api.put(`/customers/${selectedCustomer.id}`, data);
        toast.success('Cliente actualizado');
      }

      setIsDialogOpen(false);
      setSelectedCustomer(null);
      resetForm();
      fetchCustomers();
    } catch (error) {
      console.error('Error saving customer:', error);
      toast.error('Error al guardar cliente');
    }
  };

  const handleEdit = (customer) => {
    setSelectedCustomer(customer);
    setFormData({
      name: customer.name || '',
      email: customer.email || '',
      phone: customer.phone || '',
      notes: customer.notes || '',
      tags: customer.tags?.join(', ') || ''
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      notes: '',
      tags: ''
    });
  };

  if (!selectedBusiness) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-sm text-[var(--text-muted)]">Selecciona un negocio para continuar</p>
      </div>
    );
  }

  return (
    <div className="h-full p-6" style={{ background: 'var(--bg)', color: 'var(--text)' }} data-testid="customers-page">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-4xl tracking-tight font-black text-[var(--text)] mb-2 heading-font">
            Clientes
          </h1>
          <p className="text-sm text-[var(--text-muted)]">{selectedBusiness?.name} - {customers.length} clientes</p>
        </div>
      </div>

      {/* Customers Table */}
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[12px] overflow-hidden shadow-[var(--shadow-sm)]">
        <table className="w-full" data-testid="customers-table">
          <thead className="bg-[var(--surface-muted)] border-b border-[var(--border)]">
            <tr>
              <th className="px-4 py-3 text-left text-xs tracking-[0.1em] uppercase font-bold text-[var(--text-muted)]">
                Nombre
              </th>
              <th className="px-4 py-3 text-left text-xs tracking-[0.1em] uppercase font-bold text-[var(--text-muted)]">
                Plataforma
              </th>
              <th className="px-4 py-3 text-left text-xs tracking-[0.1em] uppercase font-bold text-[var(--text-muted)]">
                Email
              </th>
              <th className="px-4 py-3 text-left text-xs tracking-[0.1em] uppercase font-bold text-[var(--text-muted)]">
                Teléfono
              </th>
              <th className="px-4 py-3 text-left text-xs tracking-[0.1em] uppercase font-bold text-[var(--text-muted)]">
                Etiquetas
              </th>
              <th className="px-4 py-3 text-left text-xs tracking-[0.1em] uppercase font-bold text-[var(--text-muted)]">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody>
            {customers.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-4 py-8 text-center text-sm text-[var(--text-muted)]">
                  No hay clientes registrados
                </td>
              </tr>
            ) : (
              customers.map((customer) => (
                <tr
                  key={customer.id}
                  className="border-b border-[var(--border)] hover:bg-[var(--surface-muted)] transition-colors duration-150"
                  data-testid={`customer-row-${customer.id}`}
                >
                  <td className="px-4 py-3 text-sm text-[var(--text)] font-bold">
                    {customer.name}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2.5 py-1 rounded-full font-bold uppercase tracking-wide border ${
                      customer.platform === 'whatsapp'
                        ? 'bg-green-500/15 text-green-600 dark:text-green-400 border-green-500/30'
                        : 'bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30'
                    }`}>
                      {customer.platform}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-[var(--text-muted)]">
                    {customer.email || '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-[var(--text-muted)]">
                    {(() => {
                      const phone = formatWhatsappPhone(customer);
                      if (phone) {
                        return (
                          <button
                            type="button"
                            onClick={() => {
                              const raw = phone.replace(/\D/g, '');
                              navigator.clipboard?.writeText(raw).then(() => toast.success('Número copiado'));
                            }}
                            className={`hover:underline ${customer.platform === 'whatsapp' ? 'text-green-600 dark:text-green-400' : 'text-[var(--text)]'}`}
                            data-testid={`customer-phone-${customer.id}`}
                            title="Clic para copiar"
                          >
                            {customer.platform === 'whatsapp' ? '📱 ' : ''}{phone}
                          </button>
                        );
                      }
                      return customer.phone || '-';
                    })()}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 flex-wrap">
                      {customer.tags?.map((tag, idx) => (
                        <span
                          key={idx}
                          className="text-xs px-2 py-0.5 bg-[var(--surface-muted)] border border-[var(--border)] rounded-[12px] text-[var(--text)]"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => handleEdit(customer)}
                      className="p-2 hover:bg-[var(--surface-muted)] text-[var(--primary)] transition-colors duration-150 rounded-[12px]"
                      data-testid={`edit-customer-${customer.id}`}
                    >
                      <PencilSimple size={18} weight="bold" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Editar Cliente</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="customer-form">
            <div>
              <Label htmlFor="name">Nombre</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="customer-name-input"
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                data-testid="customer-email-input"
              />
            </div>
            <div>
              <Label htmlFor="phone">Teléfono</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                data-testid="customer-phone-input"
              />
            </div>
            <div>
              <Label htmlFor="notes">Notas</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                data-testid="customer-notes-input"
              />
            </div>
            <div>
              <Label htmlFor="tags">Etiquetas (separadas por coma)</Label>
              <Input
                id="tags"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                placeholder="vip, frecuente, mayorista"
                data-testid="customer-tags-input"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsDialogOpen(false);
                  setSelectedCustomer(null);
                  resetForm();
                }}
              >
                Cancelar
              </Button>
              <Button type="submit" data-testid="save-customer-btn">
                Guardar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Customers;
